// src/stores/messageStore.ts
// מנהל מצב הודעות - תבניות ושליחה

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useAuthStore } from './authStore';

// ===============================================
// Types
// ===============================================

export type TemplateType = 'child' | 'general'; // הודעה לילד ספציפי או הודעה כללית

export interface MessageTemplate {
  id: string;
  name: string;           // שם התבנית (לתצוגה)
  content: string;        // תוכן ההודעה (עם placeholder {childName} לתבניות ילד)
  type: TemplateType;     // סוג התבנית - לילד או כללית
  isDefault: boolean;     // תבנית ברירת מחדל או מותאמת לגן
  createdAt: string;
}

export interface ParentReadStatus {
  parentName: string;
  parentType: 'primary' | 'secondary'; // הורה ראשי או משני
  readAt: string | null;  // null = לא נקרא
}

export interface SentMessage {
  id: string;
  childPublicId?: string;  // null אם הודעה כללית
  childName?: string;
  templateId?: string;
  content: string;
  sentAt: string;
  sentTo: 'all' | 'child';
  // סטטוס קריאה לכל הורה
  readStatus: ParentReadStatus[];
}

interface MessageState {
  // Data
  templates: MessageTemplate[];
  sentMessages: SentMessage[];
  isLoadingMessages: boolean;

  // Actions - Templates
  addTemplate: (name: string, content: string, type: TemplateType) => void;
  updateTemplate: (id: string, name: string, content: string, type: TemplateType) => void;
  deleteTemplate: (id: string) => void;
  getTemplatesByType: (type: TemplateType) => MessageTemplate[];

  // Actions - Messages
  sendMessage: (params: {
    childPublicId?: string;
    childName?: string;
    templateId?: string;
    content: string;
    kindergartenPublicId: string;
    sendToMobile?: boolean; // האם לשלוח גם SMS לנייד
  }) => Promise<{ success: boolean; error?: string }>;

  // טעינת הודעות מהשרת (לטאבלט)
  loadMessagesFromServer: (kindergartenPublicId: string) => Promise<void>;

  getMessagesByChild: (childPublicId: string) => SentMessage[];
  getGeneralMessages: () => SentMessage[];

  getTemplateById: (id: string) => MessageTemplate | undefined;
}

// ===============================================
// Default Templates - מופרדים לפי סוג
// ===============================================

const DEFAULT_TEMPLATES: MessageTemplate[] = [
  // תבניות לילד ספציפי
  {
    id: 'child-1',
    name: 'עדכון הגעה',
    content: 'שלום, רצינו לעדכן ש-{childName} הגיע/ה בשלום לגן.',
    type: 'child',
    isDefault: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'child-2',
    name: 'תזכורת להביא',
    content: 'שלום הורי {childName}, נא להביא בתאריך מחר לגן: [פרט מה להביא]',
    type: 'child',
    isDefault: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'child-3',
    name: 'הודעת חום',
    content: 'שלום, רצינו לעדכן כי ל-{childName} יש חום קל. נא להתקשר לגן.',
    type: 'child',
    isDefault: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'child-4',
    name: 'פציעה קלה',
    content: 'שלום, {childName} נפל/ה ויש לו/לה שריטה קלה. טיפלנו וזה בסדר.',
    type: 'child',
    isDefault: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'child-5',
    name: 'בקשה לאיסוף',
    content: 'שלום, נבקש לאסוף את {childName} מוקדם יותר היום.',
    type: 'child',
    isDefault: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'child-6',
    name: 'הודעה אישית',
    content: 'שלום הורי {childName}, [הודעה אישית]',
    type: 'child',
    isDefault: true,
    createdAt: new Date().toISOString(),
  },
  // תבניות כלליות לכל הגן
  {
    id: 'general-1',
    name: 'הודעה כללית',
    content: 'שלום לכל ההורים, [הודעה כללית]',
    type: 'general',
    isDefault: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'general-2',
    name: 'תזכורת אירוע',
    content: 'שלום לכל ההורים, מזכירים לכם על האירוע שיתקיים בתאריך מחר בשעה [שעה]: [פרטי האירוע]',
    type: 'general',
    isDefault: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'general-3',
    name: 'עדכון מזג אוויר',
    content: 'שלום לכל ההורים, בשל מזג האוויר [פרט], נא להביא לילדים [ציוד מתאים].',
    type: 'general',
    isDefault: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'general-4',
    name: 'סגירת גן',
    content: 'שלום לכל ההורים, הגן יהיה סגור בתאריך מחר בשל [סיבה].',
    type: 'general',
    isDefault: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'general-5',
    name: 'בקשה כללית',
    content: 'שלום לכל ההורים, נבקשכם [בקשה]. תודה על שיתוף הפעולה!',
    type: 'general',
    isDefault: true,
    createdAt: new Date().toISOString(),
  },
];


// ===============================================
// Store
// ===============================================

export const useMessageStore = create<MessageState>()(
  persist(
    (set, get) => ({
      templates: DEFAULT_TEMPLATES,
      sentMessages: [],
      isLoadingMessages: false,

      addTemplate: (name, content, type) => {
        const newTemplate: MessageTemplate = {
          id: `custom-${Date.now()}`,
          name,
          content,
          type,
          isDefault: false,
          createdAt: new Date().toISOString(),
        };
        set(state => ({
          templates: [...state.templates, newTemplate],
        }));
      },

      updateTemplate: (id, name, content, type) => {
        set(state => ({
          templates: state.templates.map(t =>
            t.id === id ? { ...t, name, content, type } : t
          ),
        }));
      },

      deleteTemplate: (id) => {
        // לא ניתן למחוק תבניות ברירת מחדל
        set(state => ({
          templates: state.templates.filter(t => t.id !== id || t.isDefault),
        }));
      },

      getTemplatesByType: (type) => {
        return get().templates.filter(t => t.type === type);
      },

      sendMessage: async ({ childPublicId, childName, templateId, content, kindergartenPublicId, sendToMobile = false }) => {
        try {
          // בדיקת מצב Demo
          const isDemo = kindergartenPublicId === '550e8400-e29b-41d4-a716-446655440000';

          if (isDemo) {

            // יצירת סטטוס קריאה לדוגמה
            const readStatus: ParentReadStatus[] = childPublicId
              ? [
                  { parentName: 'הורה ראשי', parentType: 'primary', readAt: null },
                  { parentName: 'הורה משני', parentType: 'secondary', readAt: null },
                ]
              : [
                  { parentName: 'דנה כהן', parentType: 'primary', readAt: null },
                  { parentName: 'רונית לוי', parentType: 'primary', readAt: null },
                  { parentName: 'שרה ישראלי', parentType: 'primary', readAt: null },
                  { parentName: 'יעל גולן', parentType: 'primary', readAt: null },
                ];

            // שמירה בהיסטוריה מקומית
            const sentMessage: SentMessage = {
              id: `msg-${Date.now()}`,
              childPublicId,
              childName,
              templateId,
              content,
              sentAt: new Date().toISOString(),
              sentTo: childPublicId ? 'child' : 'all',
              readStatus,
            };

            set(state => ({
              sentMessages: [sentMessage, ...state.sentMessages].slice(0, 100),
            }));

            return { success: true };
          }

          // שליחה אמיתית לשרת
          const apiBaseUrl = import.meta.env.VITE_API_URL || 'https://kipikid-dev-func.azurewebsites.net/api';
          const functionKey = import.meta.env.VITE_FUNCTION_KEY;
          const token = useAuthStore.getState().token;
          const response = await fetch(`${apiBaseUrl}/kindergarten/${kindergartenPublicId}/messages`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token && { 'Authorization': `Bearer ${token}` }),
              ...(functionKey && { 'x-functions-key': functionKey }),
            },
            body: JSON.stringify({
              content,
              childPublicId: childPublicId || null,
              templateId: templateId || null,
              senderIdentifier: 'tablet',
              sendToMobile: sendToMobile,
            }),
          });

          const result = await response.json();

          if (!result.success) {
            return { success: false, error: result.message || 'שגיאה בשליחת ההודעה' };
          }

          // שמירה בהיסטוריה מקומית עם הנתונים מהשרת
          const apiData = result.data;
          const readStatus: ParentReadStatus[] = (apiData?.recipients || []).map((r: any) => ({
            parentName: r.parentName,
            parentType: 'primary' as const,
            readAt: r.readAt || null,
          }));

          const sentMessage: SentMessage = {
            id: apiData?.publicId || `msg-${Date.now()}`,
            childPublicId,
            childName: apiData?.childName || childName,
            templateId,
            content,
            sentAt: apiData?.sentAt || new Date().toISOString(),
            sentTo: childPublicId ? 'child' : 'all',
            readStatus,
          };

          set(state => ({
            sentMessages: [sentMessage, ...state.sentMessages].slice(0, 100),
          }));

          return { success: true };
        } catch (error) {
          console.error('[Message] Send failed:', error);
          return { success: false, error: 'שגיאה בשליחת ההודעה' };
        }
      },

      getMessagesByChild: (childPublicId) => {
        return get().sentMessages.filter(m => m.childPublicId === childPublicId);
      },

      getGeneralMessages: () => {
        return get().sentMessages.filter(m => m.sentTo === 'all');
      },

      getTemplateById: (id) => {
        return get().templates.find(t => t.id === id);
      },

      // טעינת הודעות מהשרת
      loadMessagesFromServer: async (kindergartenPublicId: string) => {
        set({ isLoadingMessages: true });
        try {
          const apiBaseUrl = import.meta.env.VITE_API_URL || 'https://kipikid-dev-func.azurewebsites.net/api';
          const functionKey = import.meta.env.VITE_FUNCTION_KEY;
          const token = useAuthStore.getState().token;

          const response = await fetch(`${apiBaseUrl}/kindergarten/${kindergartenPublicId}/messages`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              ...(token && { 'Authorization': `Bearer ${token}` }),
              ...(functionKey && { 'x-functions-key': functionKey }),
            },
          });

          const result = await response.json();
          console.log('[Message] Server response:', result);

          if (result.success && result.data) {
            // המרת הנתונים מהשרת לפורמט של SentMessage
            const messages: SentMessage[] = result.data.map((m: any) => ({
              id: m.publicId,
              childPublicId: m.childPublicId || undefined,
              childName: m.childName || undefined,
              content: m.content,
              sentAt: m.sentAt,
              sentTo: m.childPublicId ? 'child' : 'all',
              readStatus: (m.recipients || []).map((r: any) => ({
                parentName: r.parentName,
                parentType: 'primary' as const,
                readAt: r.readAt || null,
              })),
            }));

            set({ sentMessages: messages, isLoadingMessages: false });
            console.log('[Message] Loaded', messages.length, 'messages from server');
          } else {
            console.error('[Message] Failed to load messages:', result.message);
            set({ isLoadingMessages: false });
          }
        } catch (error) {
          console.error('[Message] Error loading messages from server:', error);
          set({ isLoadingMessages: false });
        }
      },
    }),
    {
      name: 'message-storage',
      partialize: (state) => ({
        templates: state.templates,
        sentMessages: state.sentMessages,
      }),
    }
  )
);
